import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import ReusableTable from '../../components/other/ReuseableTable'
import { can } from '../../helpers/auth'
import {
  useDeleteCampaignTypeMutation,
  useGetCampaignTypesQuery,
  useLazyGetCampaignTypesQuery
} from '../../redux/apis/campaignsManagement/types/cMTypesApiSlice'
import Loader from '../pages/loader'
import { toast } from 'react-toastify'
import EditCreateCampaignTypeModal from '../../components/campaigns/types/EditCreateCampaignTypeModal'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'

const CampaignsTypes = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const [open, setOpen] = useState(false)
  const [holder, setHolder] = useState(false)
  const [isEdit, setIsEdit] = useState(false)

  const [
    deleteType,
    { isLoading: deleteLoading, isSuccess, error: deleteError }
  ] = useDeleteCampaignTypeMutation()

  const { data, isLoading, error, isFetching } = useGetCampaignTypesQuery({
    page: pagination.current,
    page_size: pagination.pageSize,
    fields: ['id', 'type', 'description', 'is_active', 'last_updated_by'].join(
      ','
    ),
    expand: ['last_updated_by'],
    ...(search ? { search } : {}),
    ...filters
  })

  const context = useContext(AppSettings)

  let columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'TYPE',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'STATUS',
      dataIndex: 'is_active',
      key: 'is_active',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      render: text => <p>{text ? 'Active' : 'Inactive'}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {can('change_campaigntype') && (
            <button
              className='btn btn-outline-theme'
              onClick={() => handelEdit(record)}
            >
              Edit
            </button>
          )}
        </div>
      )
    }
  ]

  const handelEdit = record => {
    setOpen(true)
    setHolder(record)
    setIsEdit(true)
  }

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (deleteError?.data) {
      const errorMessages = Object.values(deleteError.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [error, deleteError])

  useEffect(() => {
    if (isSuccess) {
      toast.success('Campaign Type Deleted!')
    }
  }, [isSuccess])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || deleteLoading || isFetching} />

      <div className='flex mb-4'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetCampaignTypesQuery}
            fileName='campaignstypes'
          />
          {(can('add_campaigntype') || can('change_campaigntype')) && (
            <EditCreateCampaignTypeModal
              open={open}
              setOpen={setOpen}
              type={holder}
              isEdit={isEdit}
              setHolder={setHolder}
              setIsEdit={setIsEdit}
            />
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default CampaignsTypes
